/**
 * Petticoat Lane resilient infinite scroll.
 * Keeps numbered pagination as a no-JS and error fallback.
 */
(() => {
  window.__plCollectionLoader = true;
  const states = new WeakMap();
  let refreshTimer;

  const pageNumber = (href) => {
    try {
      return Number(new URL(href, window.location.origin).searchParams.get('page') || 1);
    } catch (_) {
      return 1;
    }
  };

  const findNextLink = (root, loadedPage = 1) => {
    if (!root) return null;

    const relNext = root.querySelector('a[rel="next"][href]');
    if (relNext) return relNext;

    const arrow = root.querySelector('.pagination-custom__arr:last-child a[href]');
    if (arrow && pageNumber(arrow.href) > loadedPage) return arrow;

    return [...root.querySelectorAll('.pagination-custom a[href]')]
      .filter((link) => pageNumber(link.href) > loadedPage)
      .sort((a, b) => pageNumber(a.href) - pageNumber(b.href))[0] || null;
  };

  const setup = (section) => {
    const products = section.querySelector('[data-products-grid]');
    const grid = products?.querySelector('.js-grid');
    if (!products || !grid) return;

    const current = states.get(section);
    if (current?.grid === grid) {
      current.check();
      return;
    }
    current?.destroy();

    const firstNext = findNextLink(products, 1);
    if (!firstNext) return;

    const paginationWrap = () =>
      products.querySelector('.pagination-custom')?.closest('.text-center');

    const sentinel = document.createElement('div');
    sentinel.className = 'collection-infinite-scroll';
    sentinel.setAttribute('aria-live', 'polite');
    sentinel.setAttribute('aria-label', 'Loading more products');
    sentinel.style.cssText = 'height:1px;width:100%;clear:both;';
    grid.insertAdjacentElement('afterend', sentinel);

    const pagination = paginationWrap();
    if (pagination) pagination.style.display = 'none';

    const state = {
      grid,
      nextUrl: firstNext.href,
      loadedPage: 1,
      loading: false,
      observer: null,
      sentinel,
      loadedUrls: new Set(),
      destroyed: false,
      scrollHandler: null,
      resizeHandler: null,
      check: null,
      destroy: null
    };

    const showPagination = () => {
      const fallback = paginationWrap();
      if (fallback) fallback.style.display = '';
    };

    const loadNext = async () => {
      if (state.destroyed || state.loading || !state.nextUrl || state.loadedUrls.has(state.nextUrl)) return;

      const requestedUrl = state.nextUrl;
      state.loading = true;
      state.loadedUrls.add(requestedUrl);
      sentinel.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(requestedUrl, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error('Unable to load more products (' + response.status + ')');

        const nextDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextSection = [...nextDocument.querySelectorAll('[data-section-type="collection"]')]
          .find((candidate) => candidate.querySelector('[data-products-grid]'));
        const nextProducts = nextSection?.querySelector('[data-products-grid]') ||
          nextDocument.querySelector('[data-products-grid]');
        const nextGrid = nextProducts?.querySelector('.js-grid');

        if (!nextGrid) throw new Error('Product grid not found on next page');

        [...nextGrid.children].forEach((item) => state.grid.appendChild(item));

        state.loadedPage = pageNumber(requestedUrl);
        const nextLink = findNextLink(nextProducts, state.loadedPage);
        state.nextUrl = nextLink?.href || '';

        const currentPagination = paginationWrap();
        const nextPagination = nextProducts.querySelector('.pagination-custom')?.closest('.text-center');

        if (currentPagination && nextPagination) {
          currentPagination.replaceWith(nextPagination);
          nextPagination.style.display = 'none';
        } else if (!state.nextUrl && currentPagination) {
          currentPagination.remove();
        }

        window.dispatchEvent(new Event('resize'));
        document.dispatchEvent(new CustomEvent('petticoatlane:products-loaded', {
          detail: { page: state.loadedPage }
        }));
      } catch (error) {
        console.error('Petticoat Lane infinite scroll:', error);
        state.nextUrl = '';
        showPagination();
        sentinel.setAttribute('aria-label', 'More products are available using page navigation');
      } finally {
        state.loading = false;
        sentinel.removeAttribute('aria-busy');
        if (!state.nextUrl) {
          state.observer?.disconnect();
        } else {
          window.requestAnimationFrame(() => state.check());
        }
      }
    };

    state.check = () => {
      if (state.destroyed || state.loading || !state.nextUrl) return;
      const rect = sentinel.getBoundingClientRect();
      if (rect.top <= window.innerHeight + 1000) loadNext();
    };

    state.scrollHandler = () => {
      window.requestAnimationFrame(state.check);
    };
    state.resizeHandler = state.scrollHandler;

    window.addEventListener('scroll', state.scrollHandler, { passive: true });
    window.addEventListener('resize', state.resizeHandler, { passive: true });

    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadNext();
      }, { rootMargin: '1000px 0px' });
      state.observer.observe(sentinel);
    }

    state.destroy = () => {
      state.destroyed = true;
      state.observer?.disconnect();
      window.removeEventListener('scroll', state.scrollHandler);
      window.removeEventListener('resize', state.resizeHandler);
      sentinel.remove();
    };

    states.set(section, state);
    state.check();
  };

  const refresh = () => {
    document.querySelectorAll('[data-section-type="collection"]').forEach(setup);
  };

  new MutationObserver(() => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 120);
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:reorder', refresh);
  window.addEventListener('pageshow', refresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }
})();